import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';

function InvIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':    return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'mail':    return <svg {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
        case 'shield':  return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'check':   return <svg {...p}><path d="M20 6 9 17l-5-5"/></svg>;
        case 'x':       return <svg {...p}><path d="M18 6 6 18M6 6l12 12"/></svg>;
        case 'star':    return <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
        default: return null;
    }
}

export default function Invitaciones() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [profileInfo, setProfileInfo]   = useState({ id: 0, username: '' });
    const [invitations, setInvitations]   = useState([]);
    const [ready, setReady]               = useState(false);
    const [acting, setActing]             = useState({});   // { [winegang_id]: 'accept'|'decline' }
    const [leaving, setLeaving]           = useState({});   // { [winegang_id]: true } — exit anim

    useEffect(() => {
        const stored = localStorage.getItem('userLogged');
        if (stored) setProfileInfo(JSON.parse(stored));
    }, []);

    const fetchInvitations = (uid) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${process.env.REACT_APP_API_URL}/findInvitation/${uid}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const data = JSON.parse(this.responseText);
                setInvitations(data.winegangs || []);
                requestAnimationFrame(() => setReady(true));
            }
        };
        xhr.send();
    };

    useEffect(() => {
        if (profileInfo.id) fetchInvitations(profileInfo.id);
    }, [profileInfo.id]);

    const dismiss = (gangId, action) => {
        if (acting[gangId]) return;
        setActing(prev => ({ ...prev, [gangId]: action }));

        const url = action === 'accept'
            ? `${process.env.REACT_APP_API_URL}/confirmInvitation/${profileInfo.id}/${gangId}`
            : `${process.env.REACT_APP_API_URL}/deleteInvitation/${profileInfo.id}/${gangId}`;

        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                setLeaving(prev => ({ ...prev, [gangId]: true }));
                setTimeout(() => {
                    setInvitations(prev => prev.filter(g => g.id !== gangId));
                    setActing(prev => { const n = { ...prev }; delete n[gangId]; return n; });
                    setLeaving(prev => { const n = { ...prev }; delete n[gangId]; return n; });
                }, 380);
            }
        };
        xhr.send();
    };

    return (
        <div className={`inv-stage${ready ? ' inv-ready' : ''}`}>
            <div className="inv-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="inv-vignette" />
            <div className="inv-grain" />

            <div className="inv-header">
                <button className="gn-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <InvIcon name="back" size={18} />
                </button>
                <div className="inv-header-center">
                    <InvIcon name="mail" size={15} />
                    <span className="inv-header-title">{t('gangs.invitations_title')}</span>
                </div>
                <div className="inv-badge-wrap">
                    {invitations.length > 0 && (
                        <span className="inv-count-badge">{invitations.length}</span>
                    )}
                </div>
            </div>

            <div className="inv-scroll">
                {invitations.length > 0 ? (
                    <div className="inv-list">
                        {invitations.map((gang, i) => (
                            <div
                                key={gang.id}
                                className={`inv-card${leaving[gang.id] ? ' inv-card-leaving' : ''}`}
                                style={{ animationDelay: `${i * 0.07}s` }}
                            >
                                <div className="inv-card-icon">
                                    <InvIcon name="shield" size={20} />
                                </div>

                                <div className="inv-card-info">
                                    <span className="inv-gang-name">{gang.winegang_name}</span>
                                    <div className="inv-gang-meta">
                                        <InvIcon name="star" size={11} />
                                        <span>{gang.average.toFixed(1)}% {t('common.average').toLowerCase()}</span>
                                    </div>
                                </div>

                                <div className="inv-card-actions">
                                    <button
                                        className={`inv-btn inv-btn-accept${acting[gang.id] === 'accept' ? ' inv-btn-loading' : ''}`}
                                        onClick={() => dismiss(gang.id, 'accept')}
                                        disabled={!!acting[gang.id]}
                                        aria-label={t('gangs.col_accept')}
                                    >
                                        {acting[gang.id] === 'accept'
                                            ? <span className="inv-spinner" />
                                            : <InvIcon name="check" size={15} />
                                        }
                                    </button>
                                    <button
                                        className={`inv-btn inv-btn-decline${acting[gang.id] === 'decline' ? ' inv-btn-loading' : ''}`}
                                        onClick={() => dismiss(gang.id, 'decline')}
                                        disabled={!!acting[gang.id]}
                                        aria-label={t('gangs.col_delete')}
                                    >
                                        {acting[gang.id] === 'decline'
                                            ? <span className="inv-spinner" />
                                            : <InvIcon name="x" size={15} />
                                        }
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : ready ? (
                    <div className="inv-empty">
                        <InvIcon name="mail" size={36} />
                        <p>{t('gangs.no_invitations')}</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
